import { phaseIsAirborne, phaseIsArrived, type FlightPhaseId } from "./flightPhase";
import type { FlightLogEntry, Mission } from "./types";

/**
 * Schedule ops status — not flight phase (taxi/climb/cruise).
 * Derived from the **current locked ticket** STD/STA plus live tracker /
 * SimConnect kinematics. Megan retime, desk push, overlap shift, and accept
 * with slipped deps overwrite `departureIso` / `eteMin`; those new fields
 * are the only schedule. Never compare against an older RFQ, OFP out, or
 * pre-retime snapshot. Cancelled always wins. Do not persist.
 */
export type FlightOpsStatus = "on_time" | "delayed" | "early" | "cancelled";

export const FLIGHT_OPS_STATUSES: readonly FlightOpsStatus[] = ["on_time", "delayed", "early", "cancelled"];

export const FLIGHT_OPS_STATUS_LABEL: Record<FlightOpsStatus, string> = {
  on_time: "On time",
  delayed: "Delayed",
  early: "Early",
  cancelled: "Cancelled",
};

export type FlightOpsTicket = {
  status?: string | null;
  /** Locked current STD on the filed ticket. */
  departureIso?: string | null;
  /** Locked current ETE — STA is STD + ETE. */
  eteMin?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
  /**
   * Stale clocks that must never drive status. Callers may pass them so
   * tests prove a retime left the old RFQ / OFP / pre-shift time behind.
   */
  priorDepartureIso?: string | null;
  inboxDepartureIso?: string | null;
  ofpSchedOutUnix?: string | null;
};

export type FlightOpsProgress = {
  now?: Date;
  phase?: FlightPhaseId | null;
  groundSpeedKt?: number | null;
  /** Projected arrival (live kinematics). Plan-only ETA is ignored. */
  etaAtUtc?: string | null;
  etaSource?: "live" | "plan" | "none" | null;
  departedAtUtc?: string | null;
  arrivedAtUtc?: string | null;
  /** Off-blocks. Delay versus STD uses this ahead of wheels-off. */
  offBlocksUtc?: string | null;
};

function parseMs(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Locked STD from the current ticket field only.
 * Ignores OFP `schedOutUnix`, Inbox RFQ origin, and any priorDepartureIso.
 */
export function ticketStdMs(
  ticket: Pick<FlightOpsTicket, "departureIso" | "priorDepartureIso" | "inboxDepartureIso" | "ofpSchedOutUnix">,
): number | null {
  void ticket.priorDepartureIso;
  void ticket.inboxDepartureIso;
  void ticket.ofpSchedOutUnix;
  return parseMs(ticket.departureIso);
}

/** Locked STA: current STD + current ETE. Same clock the dispatch table shows. */
export function ticketStaMs(ticket: Pick<FlightOpsTicket, "departureIso" | "eteMin">): number | null {
  const std = ticketStdMs(ticket);
  if (std == null) return null;
  const ete = typeof ticket.eteMin === "number" && Number.isFinite(ticket.eteMin) ? ticket.eteMin : null;
  if (ete == null || ete <= 0) return null;
  return std + ete * 60_000;
}

function isCancelled(ticket?: FlightOpsTicket | null): boolean {
  const status = ticket?.status?.trim().toLowerCase() ?? "";
  return status === "cancelled" || status === "canceled";
}

/** Off the stand / airborne / arrived. Taxi after STD is still not departed. */
function hasDeparted(ticket?: FlightOpsTicket | null, progress?: FlightOpsProgress | null): boolean {
  if (parseMs(progress?.departedAtUtc) != null || parseMs(ticket?.startedAt) != null) return true;
  const status = ticket?.status?.trim().toLowerCase() ?? "";
  if (status === "completed" || status === "complete") return true;
  const phase = progress?.phase ?? null;
  if (phase && (phaseIsAirborne(phase) || phaseIsArrived(phase) || phase === "takeoff")) return true;
  return false;
}

function hasArrived(ticket?: FlightOpsTicket | null, progress?: FlightOpsProgress | null): boolean {
  if (parseMs(progress?.arrivedAtUtc) != null || parseMs(ticket?.completedAt) != null) return true;
  const status = ticket?.status?.trim().toLowerCase() ?? "";
  if (status === "completed" || status === "complete") return true;
  const phase = progress?.phase ?? null;
  return Boolean(phase && phaseIsArrived(phase));
}

function projectedArrivalMs(ticket?: FlightOpsTicket | null, progress?: FlightOpsProgress | null): number | null {
  const actual = parseMs(progress?.arrivedAtUtc) ?? parseMs(ticket?.completedAt);
  if (actual != null) return actual;
  if (progress?.etaSource === "live") return parseMs(progress.etaAtUtc);
  return null;
}

/**
 * On time / Delayed / Early / Cancelled from **current** ticket STD/STA vs progress.
 * Cancelled beats every other label. Retime locks the new STD/STA — old 1400Z
 * after a push to 1500Z is gone.
 */
export function deriveFlightOpsStatus(input: {
  ticket?: FlightOpsTicket | null;
  progress?: FlightOpsProgress | null;
}): FlightOpsStatus {
  const ticket = input.ticket ?? null;
  const progress = input.progress ?? null;
  if (isCancelled(ticket)) return "cancelled";

  const nowMs = (progress?.now ?? new Date()).getTime();
  const std = ticketStdMs(ticket ?? {});
  const sta = ticketStaMs(ticket ?? {});
  const departed = hasDeparted(ticket, progress);
  const arrived = hasArrived(ticket, progress);
  const arrival = projectedArrivalMs(ticket, progress);
  const offBlocks = parseMs(progress?.offBlocksUtc);
  const wheelsOrStart = parseMs(progress?.departedAtUtc) ?? parseMs(ticket?.startedAt);
  const departureClock = offBlocks ?? wheelsOrStart;
  const minutesLate = (at: number) => (std == null ? 0 : Math.round((at - std) / 60_000));

  if (std != null && nowMs >= std && !departed) return "delayed";

  if (sta != null && arrival != null) {
    if (arrival < sta) return "early";
    if (arrival > sta) return "delayed";
  }

  if (sta != null && nowMs > sta && !arrived) return "delayed";

  if (
    std != null &&
    departureClock != null &&
    minutesLate(departureClock) > 0 &&
    !arrived &&
    (arrival == null || (sta != null && arrival >= sta))
  ) {
    return "delayed";
  }

  if (std != null && departed && (departureClock != null ? minutesLate(departureClock) < 0 : nowMs < std)) {
    return "early";
  }

  return "on_time";
}

export function flightOpsStatusFromMission(
  mission: (Pick<Mission, "status" | "departureIso" | "eteMin" | "startedAt" | "completedAt"> & {
    ofpAttachment?: { schedOutUnix?: string } | null;
    inboxRequestId?: string | null;
  }) | null | undefined,
  progress?: FlightOpsProgress | null,
): FlightOpsStatus {
  if (!mission) return deriveFlightOpsStatus({ ticket: null, progress });
  return deriveFlightOpsStatus({
    ticket: {
      status: mission.status,
      departureIso: mission.departureIso,
      eteMin: mission.eteMin,
      startedAt: mission.startedAt,
      completedAt: mission.completedAt,
      ofpSchedOutUnix: mission.ofpAttachment?.schedOutUnix,
    },
    progress,
  });
}

export function flightOpsStatusFromLog(
  log: Pick<FlightLogEntry, "status" | "departedAtUtc" | "arrivedAtUtc" | "plannedEteMin" | "offBlocksUtc">,
  mission?: Pick<Mission, "status" | "departureIso" | "eteMin" | "startedAt" | "completedAt"> | null,
  progress?: FlightOpsProgress | null,
): FlightOpsStatus {
  if (log.status === "cancelled" || mission?.status === "cancelled") return "cancelled";
  return deriveFlightOpsStatus({
    ticket: {
      status: mission?.status ?? log.status,
      departureIso: mission?.departureIso,
      eteMin: mission?.eteMin ?? log.plannedEteMin,
      startedAt: mission?.startedAt ?? log.departedAtUtc,
      completedAt: mission?.completedAt ?? log.arrivedAtUtc,
    },
    progress: {
      now: progress?.now,
      phase: progress?.phase,
      groundSpeedKt: progress?.groundSpeedKt,
      etaAtUtc: progress?.etaAtUtc,
      etaSource: progress?.etaSource,
      offBlocksUtc: progress?.offBlocksUtc ?? log.offBlocksUtc,
      departedAtUtc: progress?.departedAtUtc ?? log.departedAtUtc,
      arrivedAtUtc: progress?.arrivedAtUtc ?? log.arrivedAtUtc,
    },
  });
}

export function isFlightOpsStatus(value: unknown): value is FlightOpsStatus {
  return typeof value === "string" && (FLIGHT_OPS_STATUSES as readonly string[]).includes(value);
}
